import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

/**
 * Phase 2 Block C — Verify-Success page.
 *
 * After the user clicks the email-verification link they land here.
 * Three audiences:
 *   1. Mobile-browser user with the app installed → tap "Zur App öffnen",
 *      which triggers `dealalert://login?verified=true`. The Expo app
 *      catches that via Linking + replaces the route to /login with a
 *      toast.
 *   2. Mobile-browser user WITHOUT the app installed → button does
 *      nothing visible; after a 2.5s grace we surface a friendly
 *      "App nicht installiert?" hint with store links.
 *   3. Desktop user → no `dealalert://` button at all; the primary CTA
 *      is "Zum Dashboard" which routes inside the web app.
 *
 * Implementation notes:
 *   - UA sniff is intentionally simple and only used to gate UI.
 *     Backend is unchanged.
 *   - No auto-redirect via `window.location.href = "dealalert://..."`.
 *     iOS blocks that unless triggered by an explicit user gesture.
 *     We only render the button; the user has to tap.
 */
function detectMobile() {
  if (typeof navigator === "undefined") return false;
  const ua = (navigator.userAgent || "").toLowerCase();
  // Treat iPad on iPadOS 13+ as desktop UA — guard with maxTouchPoints.
  const ipadOS =
    /macintosh/i.test(ua) &&
    typeof navigator.maxTouchPoints === "number" &&
    navigator.maxTouchPoints > 1;
  return (
    /iphone|ipod|ipad|android|mobile|webos|opera mini/i.test(ua) || ipadOS
  );
}

export default function VerifySuccessPage() {
  const { t } = useTranslation("auth");
  const [isMobile] = useState(() => detectMobile());
  const [showFallback, setShowFallback] = useState(false);

  const onAppOpenClick = () => {
    // The href on the anchor still does the deep-link work; we just
    // schedule a 3-second fallback in case the app isn't installed
    // and the user is still on this page.
    setTimeout(() => setShowFallback(true), 3000);
  };

  return (
    <div className="mx-auto max-w-md py-12" data-testid="verify-success-page">
      <div className="rounded-3xl border border-emerald-500/30 bg-emerald-500/5 p-8 text-center space-y-5">
        <div className="text-emerald-400 text-5xl">✓</div>
        <h1 className="text-2xl font-semibold text-zinc-100">{t("verify.confirmedTitle")}</h1>
        <p className="text-sm text-zinc-400 leading-relaxed">{t("verify.successBody")}</p>
        <div className="flex flex-col items-stretch gap-2.5 pt-2">
          {isMobile && (
            <a
              href="dealalert://login?verified=true"
              data-testid="verify-success-app-btn"
              onClick={onAppOpenClick}
              className="rounded-md bg-emerald-500 px-5 py-2.5 text-sm font-medium text-zinc-950 hover:bg-emerald-400"
            >
              {t("verify.openApp")}
            </a>
          )}
          <Link
            to={(() => {
              try { return localStorage.getItem("da_return_to") || "/dashboard"; }
              catch { return "/dashboard"; }
            })()}
            onClick={() => { try { localStorage.removeItem("da_return_to"); } catch { /* noop */ } }}
            data-testid="verify-success-dashboard-btn"
            className={
              isMobile
                ? "rounded-md border border-zinc-700 px-5 py-2.5 text-sm text-zinc-200 hover:bg-zinc-900"
                : "rounded-md bg-emerald-500 px-5 py-2.5 text-sm font-medium text-zinc-950 hover:bg-emerald-400"
            }
          >
            {t("verify.toDashboard")}
          </Link>
        </div>
        {isMobile && showFallback && (
          <div
            className="rounded-md border border-zinc-800 bg-zinc-900/60 px-4 py-3 text-left text-xs text-zinc-400"
            data-testid="verify-success-app-fallback"
          >
            {t("verify.appNotInstalled")}{" "}
            <a
              href="https://apps.apple.com/app/dealalert/id6753146127"
              data-testid="verify-success-appstore-link"
              className="text-emerald-300 underline underline-offset-4 hover:text-emerald-200"
              target="_blank"
              rel="noopener noreferrer"
            >
              App Store
            </a>{" "}
            {t("verify.orAt")}{" "}
            <a
              href="https://play.google.com/store/apps/details?id=ai.dealalert.app"
              data-testid="verify-success-playstore-link"
              className="text-emerald-300 underline underline-offset-4 hover:text-emerald-200"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google Play
            </a>
            .
          </div>
        )}
      </div>
    </div>
  );
}
